import { z } from 'zod';
import { ActivitySubjectTypeSchema } from '../activity/activities.schemas';
import { DealPhaseSchema } from '../deal/deals.schemas';
import { LeadStatusSchema } from '../lead/leads.schemas';
import { ImapProviderSchema, MailboxProviderSchema } from '../communications/mailbox.schemas';
import { MemberStatusSchema, OrgRoleSlugSchema, WorkspaceRoleSlugSchema } from '../identity/members.schemas';
import { PhoneNumberSchema } from '../common/phone-number.schema';
import { IncludeTestWorkspacesQuerySchema } from './test-workspaces.schemas';

export const AdminMemberListItemSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  workspaceName: z.string().min(1),
  // Whether the member's workspace is a testing environment — carried on the
  // row so the operator list can badge it without a second lookup (IMM-593.2).
  isTest: z.boolean(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  createdAt: z.iso.datetime(),
});

export type AdminMemberListItem = z.infer<typeof AdminMemberListItemSchema>;

export const ListAdminMembersQuerySchema = IncludeTestWorkspacesQuerySchema;
export type ListAdminMembersQuery = z.infer<typeof ListAdminMembersQuerySchema>;

// Every role a member can hold, workspace-wide or at a Standort. The two slug
// sets are disjoint, so one flat enum is unambiguous.
export const AdminMemberRoleSlugSchema = z.enum([...WorkspaceRoleSlugSchema.options, ...OrgRoleSlugSchema.options]);
export type AdminMemberRoleSlug = z.infer<typeof AdminMemberRoleSlugSchema>;

export const AdminMemberOverviewItemSchema = AdminMemberListItemSchema.extend({
  status: MemberStatusSchema,
  roles: z.array(AdminMemberRoleSlugSchema),
  primaryOrganizationName: z.string().min(1).nullable(),
  lastActive: z.iso.datetime().nullable(),
  // Every Standort the member belongs to, not only the primary one — what the
  // operator list's Standort filter matches on (IMM-1075). Each id once, ascending.
  organizationIds: z.array(z.uuid()),
  // Mandatory onboarding items not done; 0 = fully onboarded (IMM-1100.2).
  onboardingOpenCount: z.number().int().nonnegative(),
});

export type AdminMemberOverviewItem = z.infer<typeof AdminMemberOverviewItemSchema>;

export const ListAdminMembersResponseSchema = z.object({
  data: z.array(AdminMemberOverviewItemSchema),
});

export type ListAdminMembersResponse = z.infer<typeof ListAdminMembersResponseSchema>;

export const ListAdminOrganizationMembersResponseSchema = z.object({
  data: z.array(AdminMemberListItemSchema),
});

export type ListAdminOrganizationMembersResponse = z.infer<typeof ListAdminOrganizationMembersResponseSchema>;

export const GetAdminMemberPathParamsSchema = z.object({
  id: z.uuid(),
});

export const AdminMemberAvatarUploadPathParamsSchema = z.object({
  id: z.uuid(),
  fileId: z.uuid(),
});

export const AdminMemberSummarySchema = AdminMemberListItemSchema.extend({
  userId: z.string().min(1),
  status: MemberStatusSchema,
});

export type AdminMemberSummary = z.infer<typeof AdminMemberSummarySchema>;

export const AdminMemberOrganizationSchema = z.object({
  organizationId: z.uuid(),
  name: z.string().min(1),
  roles: z.array(OrgRoleSlugSchema),
  isPrimary: z.boolean(),
});

export type AdminMemberOrganization = z.infer<typeof AdminMemberOrganizationSchema>;

export const GetAdminMemberResponseSchema = AdminMemberSummarySchema.extend({
  lastActive: z.iso.datetime().nullable(),
  avatarUrl: z.url().nullable(),
  position: z.string().nullable(),
  phone: z.string().nullable(),
  mobile: z.string().nullable(),
  workspaceRoles: z.array(WorkspaceRoleSlugSchema),
  organizations: z.array(AdminMemberOrganizationSchema),
});

export type GetAdminMemberResponse = z.infer<typeof GetAdminMemberResponseSchema>;

export const AdminMemberOrganizationAccessSchema = z
  .object({ organizationId: z.uuid(), roles: z.array(OrgRoleSlugSchema).min(1) })
  .strict();

export const UpdateAdminMemberAccessBodySchema = z
  .object({
    workspaceRole: WorkspaceRoleSlugSchema.nullable(),
    organizations: z
      .array(AdminMemberOrganizationAccessSchema)
      .refine((entries) => new Set(entries.map((entry) => entry.organizationId)).size === entries.length, {
        message: 'each Standort once',
      }),
    primaryOrganizationId: z.uuid().nullable(),
  })
  .strict();
export type UpdateAdminMemberAccessBody = z.infer<typeof UpdateAdminMemberAccessBodySchema>;

export const UpdateAdminMemberAccessResponseSchema = z.object({
  workspaceRoles: z.array(WorkspaceRoleSlugSchema),
  organizations: z.array(AdminMemberOrganizationSchema), // same order as GET /v1/admin/members/:id
});
export type UpdateAdminMemberAccessResponse = z.infer<typeof UpdateAdminMemberAccessResponseSchema>;

export const AdminMemberSignInKindSchema = z.enum(['SIGN_IN', 'SIGN_IN_FAILED', 'SIGN_OUT']);
export type AdminMemberSignInKind = z.infer<typeof AdminMemberSignInKindSchema>;

export const AdminMemberSignInSchema = z.object({
  kind: AdminMemberSignInKindSchema,
  occurredAt: z.iso.datetime(),
});
export type AdminMemberSignIn = z.infer<typeof AdminMemberSignInSchema>;

// GET /v1/admin/members/:id/sign-ins
export const ListAdminMemberSignInsResponseSchema = z.object({ data: z.array(AdminMemberSignInSchema) });
export type ListAdminMemberSignInsResponse = z.infer<typeof ListAdminMemberSignInsResponseSchema>;

export const ADMIN_MEMBER_ACTIVITY_GROUPS = [
  'SIGN_INS',
  'LEADS_AND_DEALS',
  'APPOINTMENTS',
  'NOTES_AND_TASKS',
  'ADMIN_ACTIONS',
] as const;
export const AdminMemberActivityGroupSchema = z.enum(ADMIN_MEMBER_ACTIVITY_GROUPS);
export type AdminMemberActivityGroup = z.infer<typeof AdminMemberActivityGroupSchema>;

// Every kind the timeline can ever show (PRD Fields table, in order). Declared whole
// here so the group of a kind has one source; .2 and .3 add the event variants.
export const ADMIN_MEMBER_ACTIVITY_KINDS = [
  'SIGN_IN',
  'SIGN_IN_FAILED',
  'LEAD_STATUS_CHANGED',
  'DEAL_PHASE_CHANGED',
  'APPOINTMENT_CREATED',
  'NOTE_CREATED',
  'TASK_CREATED',
  'CALL_LOGGED',
  'ACTED_ON_BEHALF',
  'EDITED_BY_OPERATOR',
] as const;
export type AdminMemberActivityKind = (typeof ADMIN_MEMBER_ACTIVITY_KINDS)[number];

export const ADMIN_MEMBER_ACTIVITY_GROUP_BY_KIND = {
  SIGN_IN: 'SIGN_INS',
  SIGN_IN_FAILED: 'SIGN_INS',
  LEAD_STATUS_CHANGED: 'LEADS_AND_DEALS',
  DEAL_PHASE_CHANGED: 'LEADS_AND_DEALS',
  APPOINTMENT_CREATED: 'APPOINTMENTS',
  NOTE_CREATED: 'NOTES_AND_TASKS',
  TASK_CREATED: 'NOTES_AND_TASKS',
  CALL_LOGGED: 'NOTES_AND_TASKS',
  ACTED_ON_BEHALF: 'ADMIN_ACTIONS',
  EDITED_BY_OPERATOR: 'ADMIN_ACTIONS',
} as const satisfies Record<AdminMemberActivityKind, AdminMemberActivityGroup>;

// GET /v1/admin/members/:id/activity
export const ListAdminMemberActivityQuerySchema = z.object({ group: AdminMemberActivityGroupSchema.optional() });
export type ListAdminMemberActivityQuery = z.infer<typeof ListAdminMemberActivityQuerySchema>;

export const AdminMemberActivityOnBehalfOfSchema = z.object({ operatorName: z.string().nullable() });

const ACTIVITY_EVENT_BASE = {
  occurredAt: z.iso.datetime(),
  onBehalfOf: AdminMemberActivityOnBehalfOfSchema.nullable(), // null: not inside a session
};

// What an operator edit changed — one per audited event (IMM-1159.4).
export const AdminMemberOperatorEditAreaSchema = z.enum(['DETAILS', 'PROFILE', 'ACCESS']);
export type AdminMemberOperatorEditArea = z.infer<typeof AdminMemberOperatorEditAreaSchema>;

export const AdminMemberActivityEventSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('SIGN_IN'), ...ACTIVITY_EVENT_BASE }),
  z.object({ kind: z.literal('SIGN_IN_FAILED'), ...ACTIVITY_EVENT_BASE }),
  z.object({
    kind: z.literal('LEAD_STATUS_CHANGED'),
    ...ACTIVITY_EVENT_BASE,
    leadLabel: z.string().nullable(),
    fromStatus: LeadStatusSchema.nullable(),
    toStatus: LeadStatusSchema,
  }),
  z.object({
    kind: z.literal('DEAL_PHASE_CHANGED'),
    ...ACTIVITY_EVENT_BASE,
    dealLabel: z.string().nullable(),
    fromPhase: DealPhaseSchema,
    toPhase: DealPhaseSchema,
  }),
  z.object({
    kind: z.literal('APPOINTMENT_CREATED'),
    ...ACTIVITY_EVENT_BASE,
    title: z.string().nullable(),
    startsAt: z.iso.datetime().nullable(),
    isAllDay: z.boolean(),
  }),
  z.object({
    kind: z.literal('NOTE_CREATED'),
    ...ACTIVITY_EVENT_BASE,
    subjectType: ActivitySubjectTypeSchema,
    subjectLabel: z.string().nullable(),
  }),
  z.object({ kind: z.literal('TASK_CREATED'), ...ACTIVITY_EVENT_BASE, title: z.string().nullable() }),
  z.object({
    kind: z.literal('CALL_LOGGED'),
    ...ACTIVITY_EVENT_BASE,
    subjectType: ActivitySubjectTypeSchema,
    subjectLabel: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('ACTED_ON_BEHALF'),
    occurredAt: z.iso.datetime(), // the session's start
    onBehalfOf: z.null(),
    operatorName: z.string().nullable(),
    reason: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('EDITED_BY_OPERATOR'),
    occurredAt: z.iso.datetime(),
    onBehalfOf: z.null(), // the operator's own act, never "on behalf of"
    operatorName: z.string().nullable(),
    area: AdminMemberOperatorEditAreaSchema,
  }),
]);
export type AdminMemberActivityEvent = z.infer<typeof AdminMemberActivityEventSchema>;

export const ListAdminMemberActivityResponseSchema = z.object({
  activeDays: z.number().int().min(0).max(30),
  data: z.array(AdminMemberActivityEventSchema).max(50),
});
export type ListAdminMemberActivityResponse = z.infer<typeof ListAdminMemberActivityResponseSchema>;

// '' and whitespace mean "clear it". Mirrors member-profiles.schemas.ts.
const emptyToNull = (value: unknown): unknown => (typeof value === 'string' && value.trim() === '' ? null : value);

// Omitted → the stored value is untouched (a tab on an older bundle sends only
// the name and the e-mail); null or '' → cleared.
const operatorProfileText = (max: number) =>
  z.preprocess(emptyToNull, z.string().trim().min(1).max(max).nullable()).optional();
const operatorProfilePhone = z.preprocess(emptyToNull, PhoneNumberSchema.nullable()).optional();

export const UpdateAdminMemberBodySchema = z.object({
  email: z.email(),
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  position: operatorProfileText(80),
  phone: operatorProfilePhone,
  mobile: operatorProfilePhone,
  // Omitted → the photo is untouched; a uuid → that confirmed upload is attached; null → removed.
  avatarMediaId: z.uuid().nullable().optional(),
});

export type UpdateAdminMemberBody = z.infer<typeof UpdateAdminMemberBodySchema>;

export const UpdateAdminMemberPathParamsSchema = z.object({
  id: z.uuid(),
});

export const UpdateAdminMemberResponseSchema = AdminMemberSummarySchema;

export type UpdateAdminMemberResponse = z.infer<typeof UpdateAdminMemberResponseSchema>;

export const AdminMemberOnboardingItemKeySchema = z.enum([
  'FIRST_SIGN_IN',
  'PROFILE_PHOTO',
  'MOBILE',
  'POSITION',
  'IMAGE_RIGHTS_CONSENT',
  'MAILBOX',
  'CALENDAR',
  'WHATSAPP',
  'ORGANIZATION_ROLE',
  'RECEIVES_LEADS',
]);

export type AdminMemberOnboardingItemKey = z.infer<typeof AdminMemberOnboardingItemKeySchema>;

export const AdminMemberOnboardingItemGroupSchema = z.enum(['ACCOUNT', 'PROFILE', 'COMMUNICATION', 'ORGANIZATION']);

export type AdminMemberOnboardingItemGroup = z.infer<typeof AdminMemberOnboardingItemGroupSchema>;

// DONE and EXPIRING count as done; NOT_APPLICABLE is neither done nor open; every other state is open.
export const AdminMemberOnboardingItemStateSchema = z.enum([
  'DONE',
  'EXPIRING',
  'MISSING',
  'EXPIRED',
  'COMPANY_MAILBOX_ONLY',
  'INVITATION_PENDING',
  'INVITATION_EXPIRED',
  'INVITATION_REVOKED',
  // CALENDAR for a member whose mailbox carries no calendar (IMAP): neither done nor open, and not mandatory.
  'NOT_APPLICABLE',
]);

export type AdminMemberOnboardingItemState = z.infer<typeof AdminMemberOnboardingItemStateSchema>;

export const AdminMemberOnboardingOrganizationSchema = z.object({
  organizationId: z.uuid(),
  name: z.string().min(1),
  roles: z.array(OrgRoleSlugSchema), // [] on RECEIVES_LEADS
});

export type AdminMemberOnboardingOrganization = z.infer<typeof AdminMemberOnboardingOrganizationSchema>;

export const AdminMemberOnboardingItemSchema = z.object({
  key: AdminMemberOnboardingItemKeySchema,
  group: AdminMemberOnboardingItemGroupSchema,
  mandatory: z.boolean(),
  state: AdminMemberOnboardingItemStateSchema,
  done: z.boolean(),
  // The date the state refers to; null when the state has none.
  at: z.iso.datetime().nullable(),
  // MOBILE: the number; POSITION: the position. Null otherwise.
  text: z.string().nullable(),
  // MAILBOX and CALENDAR while the member has an own grant; null otherwise.
  provider: MailboxProviderSchema.nullable(),
  // MAILBOX and CALENDAR — the connection's last pull-sync (IMM-233's on-demand sync). Null otherwise or never.
  lastSyncedAt: z.iso.datetime().nullable(),
  // MAILBOX on an IMAP connection: the recognised provider, null for an unrecognised one. Null otherwise.
  imapProvider: ImapProviderSchema.nullable(),
  // MAILBOX on an IMAP connection: the incoming (IMAP) server host. Null otherwise.
  imapHost: z.string().nullable(),
  // ORGANIZATION_ROLE / RECEIVES_LEADS when done: primary first, then by name. [] otherwise.
  organizations: z.array(AdminMemberOnboardingOrganizationSchema),
});

export type AdminMemberOnboardingItem = z.infer<typeof AdminMemberOnboardingItemSchema>;

export const GetAdminMemberOnboardingResponseSchema = z.object({
  items: z.array(AdminMemberOnboardingItemSchema), // always all 10, in key order above
  mandatoryTotal: z.number().int().nonnegative(), // the member's mandatory item count: 8, or 7 when CALENDAR is NOT_APPLICABLE
  mandatoryDone: z.number().int().nonnegative(),
  complete: z.boolean(), // mandatoryDone === mandatoryTotal
});

export type GetAdminMemberOnboardingResponse = z.infer<typeof GetAdminMemberOnboardingResponseSchema>;
